/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useState } from 'react';
import { MessageFormField, RequesterMessageFormFields, OnChangeInterface } from './types';
import { DestinationCheckboxFields } from './partials/DestinationCheckboxFields';
import { MessageField } from './partials/MessageField';
import { SubjectField } from './partials/SubjectField';
import { isMessageValid, isSubjectValid } from './utils';
import { useSiteAlert } from '../../hooks/useSiteAlert';
import { SiteAlertColors } from '../../slice/siteAlertsSlice';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { usePostRequesterMessageMutation } from '../../api/g17ecoApi';
import { RequesterType } from '../../types/dataShare';

const formInitialState = { subject: '', message: '', sendByEmail: true };

const isFormValid = (form: RequesterMessageFormFields): boolean => {
  if (!isSubjectValid(form.subject)) {
    return false;
  }
  if (!isMessageValid(form.message)) {
    return false;
  }
  return true;
};

interface RequesterMessageModalProps {
  requesterId: string;
  initiativeId: string;
  title?: string;
  toggle: () => void;
}

export const RequesterMessageModal = (props: RequesterMessageModalProps) => {
  const { requesterId, initiativeId, title, toggle } = props;
  const { addSiteAlert } = useSiteAlert();
  const [sendMessage] = usePostRequesterMessageMutation();
  const [form, setForm] = useState<RequesterMessageFormFields>(formInitialState);

  if (!initiativeId) {
    return null;
  }

  const onChange: OnChangeInterface = (property) => (value) => {
    setForm((currentForm) => ({
      ...currentForm,
      [property]: value,
    }));
  };

  const isValid = isFormValid(form);

  const handleSubmit = () => {
    if (!isValid) {
      return;
    }

    sendMessage({ requesterId, requesterType: RequesterType.Portfolio, initiativeId, payload: form })
      .then(() => {
        addSiteAlert({ content: 'Message Sent', timeout: 5000 });
        toggle();
      })
      .catch(() => {
        addSiteAlert({
          content: 'There was an error sending your message. Please try again',
          color: SiteAlertColors.Danger,
        });
      });
  };

  return (
    <Modal isOpen={true} toggle={toggle} backdrop='static'>
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody>
        <div className='d-flex flex-column'>
          <SubjectField subject={form.subject} onChange={onChange(MessageFormField.Subject)} />
          <MessageField message={form.message} onChange={onChange(MessageFormField.Message)} />
          <DestinationCheckboxFields sendByEmail={form.sendByEmail} onChange={onChange(MessageFormField.SendByEmail)} />
        </div>
      </ModalBody>
      <ModalFooter className='text-right'>
        <Button color='link-secondary' size='sm' className='mr-4' onClick={toggle}>
          Cancel
        </Button>
        <Button color='primary' disabled={!isValid} onClick={handleSubmit}>
          Send
        </Button>
      </ModalFooter>
    </Modal>
  );
};
